import {defineStore} from "pinia";
import {computed, reactive, ref} from "vue";
import {format} from "date-fns";
import {findIndex} from "lodash";
import {TIME_OPTIONS, DATE_OPTIONS} from '../components/TripPlanner/useTripInput'

export const MODE_LEAVING_NOW = 'now';
export const MODE_DEPART_AT = 'depart';
export const MODE_ARRIVE_BY = 'arrive';

export const TRIP_BEST_ROUTE = 'TRANSIT_ROUTING_PREFERENCE_UNSPECIFIED';
export const TRIP_LESS_WALKING = 'LESS_WALKING';
export const TRIP_FEWER_TRANSFERS = 'FEWER_TRANSFERS';

export const flags = {
    MODE_ARRIVE_BY,
    MODE_DEPART_AT,
    MODE_LEAVING_NOW,
    TRIP_BEST_ROUTE,
    TRIP_LESS_WALKING,
    TRIP_FEWER_TRANSFERS,
    TIME_OPTIONS,
    DATE_OPTIONS,
}

const validTripOptions = [
    TRIP_BEST_ROUTE,
    TRIP_LESS_WALKING,
    TRIP_FEWER_TRANSFERS,
];

export const useTripPlannerStore = defineStore('trip-planner', () => {
    const mode = ref(MODE_LEAVING_NOW)
    const timeString = ref(format(new Date(), 'HH:mm'))
    const dateString = ref(format(new Date(), 'yyyy-MM-dd'))
    const tripOption = ref(TRIP_BEST_ROUTE)
    const allFieldsOk = computed(isValid)
    const isMapVisibleOnSmallScreens = ref(true)
    const locationFrom = reactive({
        value: '', // Name of location
        location: {
            lat: null,
            lng: null,
            place_id: '',
        },
        isValid() {
            return this.value
                && this.value.trim()
                //&& (this.place_id ? this.place_id.trim() : true)
                && this.location?.lat !== undefined
                && this.location?.lng !== undefined;
        }
    })
    const locationTo = reactive({
        value: '', // Name of location
        location: {
            lat: null,
            lng: null,
            place_id: '',
        },
        isValid() {
            return this.value
                && this.value.trim()
                //&& (this.place_id ? this.place_id.trim() : true)
                && this.location?.lat !== undefined
                && this.location?.lng !== undefined;
        }
    })

    function isValid() {
        if (! tripOption.value || ! validTripOptions.includes(tripOption.value)) {
            return false;
        }

        if (! (locationIsValid(locationFrom) && locationIsValid(locationTo))) {
            return false;
        }

        if (mode.value === MODE_LEAVING_NOW) {
            return true;
        }

        if (mode.value === MODE_DEPART_AT || mode.value === MODE_ARRIVE_BY) {
            return isTimeAndDateValid();
        }

        return false;
    }

    function isTimeAndDateValid() {
        if (timeString.value.trim() === '' || dateString.value.trim() === '') {
            return false;
        }

        return isValidTime(timeString.value) && isValidDate(dateString.value);
    }

    function isValidTime(value) {
        return findIndex(TIME_OPTIONS, {value}) > -1;
    }

    function isValidDate(value) {
        return findIndex(DATE_OPTIONS, {value}) > -1;
    }

    function isTimeDateMode() {
        return mode.value === MODE_DEPART_AT || mode.value === MODE_ARRIVE_BY;
    }

    function locationIsValid(location){
        return location?.isValid()
    }

    function setMode(newMode){
        mode.value = newMode

        if (newMode === MODE_LEAVING_NOW) {
            dateString.value = '-'
            timeString.value = '-'
            return
        }

        if (isTimeDateMode() && (!isValidTime(timeString.value) || !isValidDate(dateString.value))) {
            timeString.value = TIME_OPTIONS[0]?.value ?? format(new Date(), 'HH:mm')
            dateString.value = DATE_OPTIONS[0]?.value ?? format(new Date(), 'yyyy-MM-dd')
        }
    }

    function setTimeString(newTimeString){
        timeString.value = isTimeDateMode() && !isValidTime(newTimeString)
            ? TIME_OPTIONS[0]?.value ?? format(new Date(), 'HH:mm')
            : newTimeString

        if (isTimeDateMode() && !isValidDate(dateString.value)) {
            dateString.value = DATE_OPTIONS[0]?.value ?? format(new Date(), 'yyyy-MM-dd')
        }
    }

    function setDateString(newDateString){
        dateString.value = isTimeDateMode() && !isValidDate(newDateString)
            ? DATE_OPTIONS[0]?.value ?? format(new Date(), 'yyyy-MM-dd')
            : newDateString

        if (isTimeDateMode() && !isValidTime(timeString.value)) {
            timeString.value = TIME_OPTIONS[0]?.value ?? format(new Date(), 'HH:mm')
        }
    }

    function setTripOption(newTripOption){
        tripOption.value = newTripOption
    }

    function setLocationFrom({value, location}){
        locationFrom.value = value
        locationFrom.location = {
            lng: location?.lng ? parseFloat(location.lng) : undefined,
            lat: location?.lat ? parseFloat(location.lat) : undefined,
            place_id: location.place_id ?? '',
        }
    }

    function setLocationTo({value, location}){
        locationTo.value = value
        locationTo.location = {
            lng: location?.lng ? parseFloat(location.lng) : undefined,
            lat: location?.lat ? parseFloat(location.lat) : undefined,
            place_id: location.place_id ?? '',
        }
    }

    function switchLocations() {
        const newTo = {
            value: locationFrom.value,
            location: locationFrom.location,
        };

        setLocationFrom({ ...locationTo });
        setLocationTo(newTo);
    }

    function setMapVisibilityOnSmallScreens(isVisible){
        isMapVisibleOnSmallScreens.value = isVisible
    }

    return {
        mode,
        setMode,
        timeString,
        setTimeString,
        dateString,
        setDateString,
        tripOption,
        setTripOption,
        isMapVisibleOnSmallScreens,
        setMapVisibilityOnSmallScreens,
        locationFrom,
        setLocationFrom,
        locationTo,
        setLocationTo,
        switchLocations,
        allFieldsOk
    }
})
